// vad-lite.js
// Standalone, no dependencies. Does NOT start/stop your speech recognition.
// Emits: "speechstart", "speechend", and optional "activity" events.
// Usage:
//   const vad = new VADLite(stream, { onSpeechStart: cb, onSpeechEnd: cb2 });
//   vad.start(); ... vad.stop();

export class VADLite {
  /** @param {MediaStream} stream */
  constructor(stream, opts = {}) {
    if (!stream) throw new Error("VADLite requires a MediaStream");

    // --- Options (tune as needed) ---
    this.minSpeechMs    = opts.minSpeechMs    ?? 180;   // sustain before declaring speech
    this.minSilenceMs   = opts.minSilenceMs   ?? 700;   // sustain before declaring silence
    this.openThreshold  = opts.openThreshold  ?? 0.028; // RMS to enter "speech"
    this.closeThreshold = opts.closeThreshold ?? 0.016; // RMS to return to "silence"
    this.frameSize      = opts.frameSize      ?? 1024;  // buffer size for analysis
    this.hpCutoff       = opts.hpCutoff       ?? 120;   // high-pass to remove rumble
    this.activityHz     = opts.activityHz     ?? 0;     // >0 to emit "activity" events at this rate

    // --- Optional direct callbacks (in addition to events) ---
    this.onSpeechStart  = opts.onSpeechStart  || null;
    this.onSpeechEnd    = opts.onSpeechEnd    || null;
    this.onActivity     = opts.onActivity     || null;

    // --- Internals ---
    this.stream  = stream;
    this.ctx     = null;
    this.analyser = null;
    this.state   = "silence"; // or "speech"
    this.sustainAnchor = 0;
    this.paused  = false;
    this._evt    = new (window.EventTarget || class{ addEventListener(){} removeEventListener(){} dispatchEvent(){} })();
    this._activityTimer = null;
    this._animationFrameId = null;
    
    // Detect Safari for browser-specific optimizations
    this.isSafari = this._detectSafari();
  }

  // Subscribe to events: "speechstart" | "speechend" | "activity"
  on(type, fn){ try{ this._evt.addEventListener(type, fn);}catch{} }
  off(type, fn){ try{ this._evt.removeEventListener(type, fn);}catch{} }
  _emit(type, detail){ try{ this._evt.dispatchEvent(new CustomEvent(type, { detail })); }catch{} }

  setPaused(paused){ this.paused = !!paused; }

  updateOptions(partial = {}) {
    Object.assign(this, {
      ...(partial.minSpeechMs    !== undefined ? {minSpeechMs: partial.minSpeechMs} : {}),
      ...(partial.minSilenceMs   !== undefined ? {minSilenceMs: partial.minSilenceMs} : {}),
      ...(partial.openThreshold  !== undefined ? {openThreshold: partial.openThreshold} : {}),
      ...(partial.closeThreshold !== undefined ? {closeThreshold: partial.closeThreshold} : {}),
      ...(partial.frameSize      !== undefined ? {frameSize: partial.frameSize} : {}),
      ...(partial.hpCutoff       !== undefined ? {hpCutoff: partial.hpCutoff} : {}),
      ...(partial.activityHz     !== undefined ? {activityHz: partial.activityHz} : {}),
    });
  }

  _detectSafari() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    return isSafari || isIOS;
  }

  // Task #274 (iOS jetsam): `start(stream)` now accepts a NEW MediaStream so
  // one VADLite instance (and ONE AudioContext) can serve every mic session
  // of the page's lifetime. Previously each mic-on created a fresh
  // AudioContext and each mic-off closed it — iOS Safari has a small
  // AudioContext budget and create/close churn under memory pressure was
  // part of the mic-session jetsam picture. Now: stop() suspends the
  // context and tears down only the per-stream graph; dispose() closes it.
  start(stream = null) {
    if (stream && stream !== this.stream) {
      this.stream = stream;
      // New capture stream → the old source node is bound to dead tracks.
      this._teardownGraph();
    }
    if (this._running && this.analyser) return;

    // Create the AudioContext ONCE and reuse it across mic sessions.
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }

    // Safari sometimes starts (or resumes from stop()) in suspended state
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(err => {
        console.warn('[VAD] Failed to resume AudioContext:', err);
      });
    }

    // Build the per-stream analysis graph fresh each start.
    this._teardownGraph();
    const src = this.ctx.createMediaStreamSource(this.stream);
    const hp  = this.ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = this.hpCutoff;

    // Use AnalyserNode instead of deprecated ScriptProcessor for Safari compatibility
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = this.frameSize;
    this.analyser.smoothingTimeConstant = 0.3; // Slight smoothing for stability
    
    // Connect audio graph
    src.connect(hp);
    hp.connect(this.analyser);
    this._srcNode = src;
    this._hpNode = hp;
    
    // Safari: Don't connect to destination to avoid feedback issues
    // The analyser node doesn't need output to work
    
    // Create buffer for time domain data
    this.dataArray = new Float32Array(this.analyser.fftSize);

    // Optional periodic "activity" pings with current RMS (for visualizers)
    if (this.activityHz > 0) {
      const intervalMs = Math.max(50, Math.round(1000 / this.activityHz));
      this._activityTimer = setInterval(() => {
        if (!this._latestRMS || this.paused) return;
        const detail = { rms: this._latestRMS, state: this.state, ts: performance.now() };
        if (this.onActivity) try { this.onActivity(detail); } catch {}
        this._emit("activity", detail);
      }, intervalMs);
    }

    this._latestRMS = 0;
    this.sustainAnchor = performance.now();

    // Start the analysis loop using requestAnimationFrame (Safari compatible)
    this._running = true;
    this._startAnalysisLoop();
    
    console.log('[VAD] Started with', this.isSafari ? 'Safari-optimized' : 'standard', 'AnalyserNode implementation');
  }

  // Disconnect the per-stream nodes (source → highpass → analyser).
  _teardownGraph() {
    try { if (this._srcNode) this._srcNode.disconnect(); } catch {}
    try { if (this._hpNode) this._hpNode.disconnect(); } catch {}
    try { if (this.analyser) this.analyser.disconnect(); } catch {}
    this._srcNode = null;
    this._hpNode = null;
    this.analyser = null;
    this.dataArray = null;
  }

  _startAnalysisLoop() {
    // Never run two loops (e.g. start() rebinding to a new stream while a
    // previous loop is still scheduled).
    if (this._animationFrameId) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }
    const analyze = () => {
      if (!this.analyser || this.paused) {
        // Keep running the loop even when paused, just don't process
        if (this.analyser) {
          this._animationFrameId = requestAnimationFrame(analyze);
        }
        return;
      }

      // Get time domain data from analyser
      this.analyser.getFloatTimeDomainData(this.dataArray);
      
      // Calculate RMS (Root Mean Square) from time domain data
      let sum = 0;
      for (let i = 0; i < this.dataArray.length; i++) {
        sum += this.dataArray[i] * this.dataArray[i];
      }
      const rms = Math.sqrt(sum / this.dataArray.length);
      this._latestRMS = rms;

      const now = performance.now();
      
      if (this.state === "silence") {
        if (rms >= this.openThreshold) {
          // sustain timer
          if (now - this.sustainAnchor >= this.minSpeechMs) {
            this.state = "speech";
            this.sustainAnchor = now;
            const detail = { rms, ts: now };
            if (this.onSpeechStart) try { this.onSpeechStart(detail); } catch {}
            this._emit("speechstart", detail);
          }
        } else {
          this.sustainAnchor = now;
        }
      } else { // speech
        if (rms <= this.closeThreshold) {
          if (now - this.sustainAnchor >= this.minSilenceMs) {
            this.state = "silence";
            this.sustainAnchor = now;
            const detail = { rms, ts: now };
            if (this.onSpeechEnd) try { this.onSpeechEnd(detail); } catch {}
            this._emit("speechend", detail);
          }
        } else {
          this.sustainAnchor = now;
        }
      }

      // Continue the loop
      this._animationFrameId = requestAnimationFrame(analyze);
    };

    // Start the analysis loop
    this._animationFrameId = requestAnimationFrame(analyze);
  }

  // Task #274: stop() no longer CLOSES the AudioContext — it suspends it
  // and keeps it for the next mic session (see start() comment). Call
  // dispose() for a true teardown.
  async stop() {
    this._running = false;

    // Cancel animation frame loop
    if (this._animationFrameId) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }
    
    // Clear activity timer
    if (this._activityTimer) { 
      clearInterval(this._activityTimer); 
      this._activityTimer = null; 
    }
    
    // Tear down the per-stream graph; keep the context (suspended).
    this._teardownGraph();
    try {
      if (this.ctx && this.ctx.state !== 'closed') await this.ctx.suspend();
    } catch {}
  }

  // Full teardown: also closes the shared AudioContext.
  async dispose() {
    await this.stop();
    try { if (this.ctx) await this.ctx.close(); } catch {}
    this.ctx = null;
  }
}
